import type { FieldTree } from '@angular/forms/signals';

/**
 * Minimal `data` shape a widget must satisfy to install
 * {@link visibilityPlugin}. Schema twin in entity-core:
 * `visibilityPluginShapeLegacy` (`widget/common/schemas/legacy/visibility.ts`) —
 * spread it into the widget's data schema.
 */
export type VisibilityPluginData = {
    hideOnDesktop: boolean;
    hideOnTablet: boolean;
    hideOnMobile: boolean;
};

export type VisibilityApi = {
    readonly hideOnDesktop: FieldTree<boolean>;
    readonly hideOnTablet: FieldTree<boolean>;
    readonly hideOnMobile: FieldTree<boolean>;
    setHideOnDesktop: (value: boolean) => void;
    setHideOnTablet: (value: boolean) => void;
    setHideOnMobile: (value: boolean) => void;
};

/**
 * Per-device visibility flags (`hideOnDesktop`/`hideOnTablet`/`hideOnMobile`)
 * as FieldTree accessors + setter sugar. Receives the form's `data` FieldTree
 * **lazily** (`() => _raw.data`) and exposes getters, so tree paths only
 * materialise on consumer access — compose with
 * {@link import('../../core/compose-form').composeForm}, never object spread.
 */
export function visibilityPlugin<D extends VisibilityPluginData>(data: () => FieldTree<D>): VisibilityApi {
    // FieldTree is invariant (writable signals), so the narrowing is done via
    // an explicit cast — safe by the `D extends VisibilityPluginData` constraint.
    const d = data as unknown as () => FieldTree<VisibilityPluginData>;
    return {
        get hideOnDesktop() {
            return d().hideOnDesktop;
        },
        setHideOnDesktop: (value: boolean) => {
            d().hideOnDesktop().value.set(value);
        },
        get hideOnTablet() {
            return d().hideOnTablet;
        },
        setHideOnTablet: (value: boolean) => {
            d().hideOnTablet().value.set(value);
        },
        get hideOnMobile() {
            return d().hideOnMobile;
        },
        setHideOnMobile: (value: boolean) => {
            d().hideOnMobile().value.set(value);
        },
    };
}
